import React from 'react';
import { useIntl } from 'react-intl';

import { DeleteDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';

import { NonFinancialDriverTreeNode } from '../../types';
import { NonFinancialDriverType } from '../../../core/api/types/non-financial-drivers';
import { useNonFinancialDriversApiView } from '../../hooks/useNonFinancialDriversApiView';
import { useRouteIds } from '../../../hooks/useRouteIds';

interface DeleteForecastDialogProps {
  row: NonFinancialDriverTreeNode;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteDriverModal = ({ row, isOpen, onClose }: DeleteForecastDialogProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId } = useRouteIds();

  const { isDeletingDriver, deleteDriver, errorDeletingDriver } = useNonFinancialDriversApiView(
    workspaceId,
    organisationId
  );

  const { screen, handleAction, handleClose } = useDialogWizardState({
    isLoading: isDeletingDriver,
    error: errorDeletingDriver,
    onAction: () => deleteDriver(row.id),
    onClose
  });

  return (
    <DeleteDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onDelete={handleAction}
      screen={screen}
      title={
        row.type === NonFinancialDriverType.GROUP
          ? intl.formatMessage({ id: 'predict.delete_group', defaultMessage: 'Delete group' })
          : intl.formatMessage({ id: 'predict.delete_driver', defaultMessage: 'Delete driver' })
      }
      subtitle={row.name}
      thingsToKnow={
        row.type === NonFinancialDriverType.GROUP
          ? [
              intl.formatMessage({
                id: 'predict.drivers.delete.things_to_know.drivers_added_back',
                defaultMessage: 'All drivers within the group will be added back to the drivers list'
              }),
              intl.formatMessage({
                id: 'predict.driver.delete.group_not_recoverable',
                defaultMessage: 'Deleting a group is not recoverable'
              })
            ]
          : [
              intl.formatMessage({
                id: 'predict.driver.delete.driver_not_recoverable',
                defaultMessage: 'Deleting a driver is not recoverable'
              })
            ]
      }
      deleteDescription={
        row.type === NonFinancialDriverType.GROUP
          ? intl.formatMessage({
              id: 'predict.drivers.delete_group_description',
              defaultMessage: 'Are you sure you want to delete this group?'
            })
          : intl.formatMessage({
              id: 'predict.drivers.delete_driver_description',
              defaultMessage: 'Are you sure you want to delete this driver?'
            })
      }
      loadingMessage={
        row.type === NonFinancialDriverType.GROUP
          ? intl.formatMessage({
              id: 'common.deleting_group',
              defaultMessage: 'Deleting group...'
            })
          : intl.formatMessage({
              id: 'common.deleting_driver',
              defaultMessage: 'Deleting driver...'
            })
      }
      successMessage={
        row.type === NonFinancialDriverType.GROUP
          ? intl.formatMessage({
              id: 'predict.drivers.group_deleted',
              defaultMessage: 'Group deleted'
            })
          : intl.formatMessage({
              id: 'predict.drivers.driver_deleted',
              defaultMessage: 'Driver deleted'
            })
      }
      errorMessage={errorDeletingDriver?.message}
      dataTestId="delete-group-dialog"
    />
  );
};
